import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation as useReactRouterDomLocation, useNavigate, useParams } from 'react-router-dom'

import { deliveryMethodNames as deliveryMethodLabels } from '@reveni-io/reveni-ui'

import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import AppContext from '@/common/context/app.context'
import routes from '@/Router/routes.constants'

import AddressSection from './AddressSection/AddressSection'
import DefaultPackagingSection from './DefaultPackagingSection/DefaultPackagingSection'
import useLocation from './hooks/useLocation/useLocation'
import InventorySection from './InventorySection/InventorySection'
import LocationDetailConfigurationSkeleton from './LocationDetailConfigurationSkeleton/LocationDetailConfigurationSkeleton'
import LocationUsedInSection from './LocationUsedInSection/LocationUsedInSection'
import TypeSection from './TypeSection/TypeSection'
import * as Styles from './styles'

const LocationDetailConfiguration = () => {
  const { t, i18n } = useTranslation()
  const { info } = useContext(AppContext)
  const { id } = useParams()
  const reactRouterDomLocation = useReactRouterDomLocation()

  const {
    type,
    setType,
    fetchingRegions,
    regions,
    provinces,
    onChangeCountry,
    setProvince,
    internalName,
    setInternalName,
    publicName,
    setPublicName,
    line1,
    setLine1,
    line2,
    setLine2,
    houseNumber,
    setHouseNumber,
    country,
    province,
    locality,
    setLocality,
    postalCode,
    setPostalCode,
    isFormValid,
    onSaveChanges,
    creatingLocation,
    hasChanges,
    revertChanges,
    editingLocation,
    deliveryMethods,
    gettingDeliveryMethods,
    phone,
    setPhone,
    phonePrefix,
    setPhonePrefix,
    shippingMethods,
    gettingShippingMethods,
    inventoryLocationId,
    setInventoryLocationId,
    inventoryLocations,
    fetchingInventoryLocations,
    usedInventoryLocationIds,
    packaging,
    fetchingPackaging,
    defaultPackagingId,
    setDefaultPackagingId,
  } = useLocation({
    id,
    storeId: info?.selectedStore?.id,
    selectedLocation: reactRouterDomLocation.state?.selectedLocation,
  })
  const navigate = useNavigate()

  const onSelectDeliveryMethod = deliveryMethod => {
    const deliveryMethodName = deliveryMethod?.name?.[i18n.language] || t(deliveryMethodLabels[deliveryMethod?.type])

    navigate(routes.configurationPage.logistics.deliveryMethodDetailConfiguration.replace(':id', deliveryMethod.id), {
      state: { childPageTitle: deliveryMethodName, selectedDeliveryMethod: deliveryMethod },
    })
  }

  const onSelectShippingMethod = shippingMethod => {
    navigate(routes.configurationPage.logistics.shippingMethodDetailConfiguration.replace(':id', shippingMethod.id), {
      state: { childPageTitle: shippingMethod?.internal_name, selectedShippingMethod: shippingMethod },
    })
  }

  const isLoading =
    fetchingRegions ||
    gettingDeliveryMethods ||
    gettingShippingMethods ||
    fetchingInventoryLocations ||
    fetchingPackaging

  if (isLoading) {
    return <LocationDetailConfigurationSkeleton />
  }

  return (
    <>
      <Styles.MainContainer>
        {hasChanges && <UnsavedChangesAlert />}
        <TypeSection
          type={type}
          setType={setType}
          internalName={internalName}
          setInternalName={setInternalName}
          publicName={publicName}
          setPublicName={setPublicName}
        />
        <AddressSection
          regions={regions}
          provinces={provinces}
          country={country}
          province={province}
          locality={locality}
          postalCode={postalCode}
          line1={line1}
          line2={line2}
          houseNumber={houseNumber}
          onChangeCountry={onChangeCountry}
          setProvince={setProvince}
          setLine1={setLine1}
          setLine2={setLine2}
          setHouseNumber={setHouseNumber}
          setLocality={setLocality}
          setPostalCode={setPostalCode}
          phone={phone}
          setPhone={setPhone}
          phonePrefix={phonePrefix}
          setPhonePrefix={setPhonePrefix}
        />
        <InventorySection
          inventoryLocations={inventoryLocations}
          value={inventoryLocationId}
          onChange={setInventoryLocationId}
          usedInventoryLocationIds={usedInventoryLocationIds}
        />
        <DefaultPackagingSection packaging={packaging} value={defaultPackagingId} onChange={setDefaultPackagingId} />
        <LocationUsedInSection
          locationId={id}
          deliveryMethods={deliveryMethods}
          shippingMethods={shippingMethods}
          regions={regions}
          onSelectDeliveryMethod={onSelectDeliveryMethod}
          onSelectShippingMethod={onSelectShippingMethod}
          allowsShipments={info?.selectedStore?.preferences?.allows_shipments}
        />
      </Styles.MainContainer>
      <Styles.ActionsContainer>
        <SaveChangesButtons
          loading={creatingLocation || editingLocation}
          disabled={!isFormValid() || creatingLocation || editingLocation}
          onSave={onSaveChanges}
          hasChanges={hasChanges}
          onRevertChanges={revertChanges}
        />
      </Styles.ActionsContainer>
    </>
  )
}

export default LocationDetailConfiguration
